import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Space, Tag, Modal, Form, Input, InputNumber, Select, message, Popconfirm, Tooltip, Descriptions, Badge, Empty } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, CheckCircleOutlined, CloseCircleOutlined, CloudServerOutlined, CrownOutlined } from '@ant-design/icons';
import { adminApi } from '../../services/api';

const Nodes = () => {
    const [nodes, setNodes] = useState([]);
    const [clusters, setClusters] = useState([]);
    const [selectedCluster, setSelectedCluster] = useState(null);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [ipModalVisible, setIpModalVisible] = useState(false);
    const [editingNode, setEditingNode] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [ipSubmitting, setIpSubmitting] = useState(false);
    const [form] = Form.useForm();
    const [ipForm] = Form.useForm();

    const fetchClusters = async () => {
        try {
            const res = await adminApi.get('/webmail/clusters');
            if (res.data.code === 0) {
                const list = res.data.data.list || [];
                setClusters(list);
                // 自动选择默认集群
                const defaultCluster = list.find(c => c.is_default);
                if (defaultCluster) {
                    setSelectedCluster(defaultCluster.id);
                } else if (list.length > 0) {
                    setSelectedCluster(list[0].id);
                }
            }
        } catch (error) {
            message.error('获取集群列表失败');
        }
    };

    const fetchNodes = async () => {
        if (!selectedCluster) return;
        setLoading(true);
        try {
            const res = await adminApi.get('/webmail/nodes', { params: { cluster_id: selectedCluster } });
            if (res.data.code === 0) {
                setNodes(res.data.data.list || []);
            }
        } catch (error) {
            message.error('获取节点列表失败');
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchClusters();
    }, []);

    useEffect(() => {
        if (selectedCluster) {
            fetchNodes();
        }
    }, [selectedCluster]);

    const handleAdd = () => {
        setEditingNode(null);
        form.resetFields();
        form.setFieldsValue({
            port: 25,
            node_type: 'mail',
            status: 'offline',
            priority: 100,
            api_port: 8089,
            is_master: false,
        });
        setModalVisible(true);
    };

    const handleEdit = (record) => {
        setEditingNode(record);
        form.setFieldsValue(record);
        setModalVisible(true);
    };

    const handleEditIPPool = (record) => {
        setEditingNode(record);
        ipForm.setFieldsValue({
            ipv4_pool: (record.ipv4_pool || []).join('\n'),
            ipv6_pool: (record.ipv6_pool || []).join('\n'),
        });
        setIpModalVisible(true);
    };

    const handleDelete = async (id) => {
        try {
            const res = await adminApi.delete(`/webmail/nodes/${id}`, { params: { cluster_id: selectedCluster } });
            if (res.data.code === 0) {
                message.success('删除成功');
                fetchNodes();
            } else {
                message.error(res.data.message || '删除失败');
            }
        } catch (error) {
            message.error('删除失败');
        }
    };

    const handleSubmit = async (values) => {
        setSubmitting(true);
        try {
            const data = { ...values, cluster_id: selectedCluster };
            if (editingNode) {
                const res = await adminApi.put(`/webmail/nodes/${editingNode.id}`, data);
                if (res.data.code === 0) {
                    message.success('更新成功');
                    setModalVisible(false);
                    fetchNodes();
                } else {
                    message.error(res.data.message || '更新失败');
                }
            } else {
                const res = await adminApi.post('/webmail/nodes', data);
                if (res.data.code === 0) {
                    message.success('创建成功');
                    setModalVisible(false);
                    fetchNodes();
                } else {
                    message.error(res.data.message || '创建失败');
                }
            }
        } catch (error) {
            message.error('操作失败');
        } finally {
            setSubmitting(false);
        }
    };

    const handleIPPoolSubmit = async (values) => {
        setIpSubmitting(true);
        try {
            const ipv4_pool = values.ipv4_pool ? values.ipv4_pool.split('\n').map(ip => ip.trim()).filter(ip => ip) : [];
            const ipv6_pool = values.ipv6_pool ? values.ipv6_pool.split('\n').map(ip => ip.trim()).filter(ip => ip) : [];

            const res = await adminApi.put(`/webmail/nodes/${editingNode.id}/ip-pool`, {
                cluster_id: selectedCluster,
                ipv4_pool,
                ipv6_pool,
            });
            if (res.data.code === 0) {
                message.success('更新成功');
                setIpModalVisible(false);
                fetchNodes();
            } else {
                message.error(res.data.message || '更新失败');
            }
        } catch (error) {
            message.error('更新失败');
        } finally {
            setIpSubmitting(false);
        }
    };

    const handleStatusChange = async (id, status) => {
        try {
            const res = await adminApi.put(`/webmail/nodes/${id}/status`, {
                cluster_id: selectedCluster,
                status,
            });
            if (res.data.code === 0) {
                message.success('状态已更新');
                fetchNodes();
            } else {
                message.error(res.data.message || '更新失败');
            }
        } catch (error) {
            message.error('更新失败');
        }
    };

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            width: 180,
            render: (text, record) => (
                <Space>
                    {record.is_master ? <CrownOutlined style={{ color: '#faad14' }} /> : <CloudServerOutlined />}
                    {text}
                </Space>
            ),
        },
        {
            title: '主机名',
            dataIndex: 'hostname',
        },
        {
            title: 'IP地址',
            dataIndex: 'ip_addr',
            render: (ip, record) => `${ip}:${record.port}`,
        },
        {
            title: '类型',
            dataIndex: 'node_type',
            render: (type) => (
                <Tag color={type === 'mail' ? 'blue' : 'purple'}>{type}</Tag>
            ),
        },
        {
            title: '状态',
            dataIndex: 'status',
            render: (status, record) => (
                <Select
                    size="small"
                    value={status}
                    style={{ width: 120 }}
                    onChange={(v) => handleStatusChange(record.id, v)}
                >
                    <Select.Option value="online"><Badge status="success" text="在线" /></Select.Option>
                    <Select.Option value="offline"><Badge status="error" text="离线" /></Select.Option>
                    <Select.Option value="maintenance"><Badge status="warning" text="维护中" /></Select.Option>
                </Select>
            ),
        },
        {
            title: 'IPv4池',
            dataIndex: 'ipv4_pool',
            render: (pool) => (pool && pool.length > 0) ? (
                <Tooltip title={pool.join(', ')}>
                    <Tag color="cyan">{pool.length} 个IP</Tag>
                </Tooltip>
            ) : <Tag>无</Tag>,
        },
        {
            title: 'IPv6池',
            dataIndex: 'ipv6_pool',
            render: (pool) => (pool && pool.length > 0) ? (
                <Tooltip title={pool.join(', ')}>
                    <Tag color="geekblue">{pool.length} 个IP</Tag>
                </Tooltip>
            ) : <Tag>无</Tag>,
        },
        {
            title: '优先级',
            dataIndex: 'priority',
            width: 80,
        },
        {
            title: '操作',
            width: 240,
            render: (_, record) => (
                <Space>
                    <Button size="small" onClick={() => handleEditIPPool(record)}>IP池</Button>
                    <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)}>编辑</Button>
                    <Popconfirm
                        title="确定删除此节点吗？"
                        description="删除后该节点将从集群中移除，此操作不可恢复"
                        okText="删除"
                        okButtonProps={{ danger: true }}
                        onConfirm={() => handleDelete(record.id)}
                        disabled={record.is_master}
                    >
                        <Button size="small" danger icon={<DeleteOutlined />} disabled={record.is_master}>删除</Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <Card
            title="邮件节点管理"
            extra={
                <Space>
                    <Select
                        style={{ width: 200 }}
                        value={selectedCluster}
                        onChange={setSelectedCluster}
                        placeholder="选择集群"
                    >
                        {clusters.map(c => (
                            <Select.Option key={c.id} value={c.id}>
                                {c.name} {c.is_default && '(默认)'}
                            </Select.Option>
                        ))}
                    </Select>
                    <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd} disabled={!selectedCluster}>
                        添加节点
                    </Button>
                </Space>
            }
        >
            <Table
                columns={columns}
                dataSource={nodes}
                rowKey="id"
                loading={loading}
                pagination={false}
                scroll={{ x: 'max-content' }}
                locale={{ emptyText: <Empty description={selectedCluster ? '暂无节点，点击右上角“添加节点”创建' : '请先选择集群'} /> }}
            />

            {/* 编辑节点 Modal */}
            <Modal
                title={editingNode ? '编辑节点' : '添加节点'}
                open={modalVisible}
                onCancel={() => setModalVisible(false)}
                footer={null}
                width={600}
            >
                <Form form={form} layout="vertical" onFinish={handleSubmit}>
                    {!editingNode && (
                        <Form.Item
                            name="id"
                            label="节点ID"
                            rules={[{ required: true, message: '请输入节点ID' }]}
                        >
                            <Input placeholder="如：mail-main, relay-1.2.3.4" />
                        </Form.Item>
                    )}
                    <Form.Item
                        name="hostname"
                        label="主机名"
                        rules={[{ required: true, message: '请输入主机名' }]}
                    >
                        <Input placeholder="如：mail.example.com" />
                    </Form.Item>
                    <Form.Item
                        name="ip_addr"
                        label="IP地址"
                        rules={[{ required: true, message: '请输入IP地址' }]}
                    >
                        <Input placeholder="如：192.168.1.1" />
                    </Form.Item>
                    <Space>
                        <Form.Item name="port" label="端口">
                            <InputNumber min={1} max={65535} />
                        </Form.Item>
                        <Form.Item name="api_port" label="API端口">
                            <InputNumber min={1} max={65535} />
                        </Form.Item>
                        <Form.Item name="priority" label="优先级">
                            <InputNumber min={1} max={1000} />
                        </Form.Item>
                    </Space>
                    <Space>
                        <Form.Item name="node_type" label="类型">
                            <Select style={{ width: 120 }}>
                                <Select.Option value="mail">Mail</Select.Option>
                                <Select.Option value="relay">Relay</Select.Option>
                            </Select>
                        </Form.Item>
                        <Form.Item name="status" label="状态">
                            <Select style={{ width: 120 }}>
                                <Select.Option value="online">在线</Select.Option>
                                <Select.Option value="offline">离线</Select.Option>
                                <Select.Option value="maintenance">维护</Select.Option>
                            </Select>
                        </Form.Item>
                    </Space>
                    <Form.Item name="api_key" label="API密钥">
                        <Input.Password placeholder="节点API密钥" />
                    </Form.Item>
                    <Form.Item>
                        <Space>
                            <Button type="primary" htmlType="submit" loading={submitting}>保存</Button>
                            <Button onClick={() => setModalVisible(false)} disabled={submitting}>取消</Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>

            {/* IP池编辑 Modal */}
            <Modal
                title={`编辑IP池 - ${editingNode?.id}`}
                open={ipModalVisible}
                onCancel={() => setIpModalVisible(false)}
                footer={null}
                width={500}
            >
                <Form form={ipForm} layout="vertical" onFinish={handleIPPoolSubmit}>
                    <Form.Item
                        name="ipv4_pool"
                        label="IPv4 地址池（每行一个）"
                    >
                        <Input.TextArea rows={4} placeholder="217.216.40.182&#10;217.216.40.183" />
                    </Form.Item>
                    <Form.Item
                        name="ipv6_pool"
                        label="IPv6 地址池（每行一个）"
                    >
                        <Input.TextArea rows={4} placeholder="2001:db8::1&#10;2001:db8::2" />
                    </Form.Item>
                    <Form.Item>
                        <Space>
                            <Button type="primary" htmlType="submit" loading={ipSubmitting}>保存</Button>
                            <Button onClick={() => setIpModalVisible(false)} disabled={ipSubmitting}>取消</Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </Card>
    );
};

export default Nodes;
